import { UseQueryResult } from '@tanstack/react-query';
import { isEmpty, noop, union, uniq } from 'lodash-es';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { Actions } from 'react-use/lib/useSet';
import { VenueMapContentProps } from 'src/components/Events/VenueMap/VenueMapProps';
import {
  ListingPricingInputWithId,
  useAutoPricingPreview,
} from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useListingGroupAutoPricePreview } from 'src/hooks/api/useListingGroupAutoPricePreview';
import { useComparableListingsVenueMapColor } from 'src/hooks/useComparableListingsVenueMapColor';
import { useGetCompListings } from 'src/hooks/useGetCompListings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserCanSetPrice } from 'src/hooks/useUserHasListingPermissions';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { getMatchingSectionRow } from 'src/utils/seatScoreUtils';
import { mapToSelectedSectionSettings } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import {
  AutoPricingInputs,
  BulkListingPriceCalculationResult,
  CompListing,
  Event,
  Feature,
  Listing,
  ListingDetailsAutoPricingSectionUpdates,
  ListingGroup,
  ListingGroupAutoPriceSettings,
  ListingPriceCalculation,
  ListingPriceCalculationsInEventInput,
  SectionInfo,
  SectionRowScoreRecommendations,
  SelectedSectionSettings,
} from 'src/WebApiController';
import {
  createContext as createContextWithSelector,
  useContext as useContextWithSelector,
} from 'use-context-selector';

import { useAppContext } from '../AppContext';
import { useEventMapContextVenueMapInfoSelector } from '../EventMapContext';
import { useLocalizationContext } from '../LocalizationContext';
import { usePricingSectionScoreRecommendations } from './InventoryEventDetailContext.hooks';
import { getListingPriceCalculationsInEventInputFromGroupSettings } from './InventoryEventDetailContext.utils';

export type IInventoryEventDetailContext = {
  event?: Event | undefined;
  eventListings?: Listing[] | null;
  selectedListingIds: number[];
  recommendedSectionIds?: number[]; // all recommending section ids
  recommendingSectionIds?: number[]; // section ids recommending but not taken
  selectedListing?: Listing | undefined;
  selectedGroup?: ListingGroup | undefined;
  flattenedListingGroup: Listing[] | undefined;
  compQuantityFilters: string[];
  setCompQuantityFilters: React.Dispatch<React.SetStateAction<string[]>>;
  priceCalcQuery: UseQueryResult<ListingPriceCalculation | null, Error> | null;
  listingGroupPriceCalcQuery: UseQueryResult<
    BulkListingPriceCalculationResult | null,
    Error
  > | null;
  compListings?: CompListing[] | undefined;
  filterSections: SectionInfo[];
  compListingSectionIds: number[];
  getColor: NonNullable<VenueMapContentProps['getColor']>;
  splitChoices: Record<string, string>;
  isLoading: boolean;
  sectionRowScoreRecommendations?: SectionRowScoreRecommendations;
  isRecommendationDeclined: boolean;
  onDeclineRecommendation: () => void;
  onToggleRecommendingSectionIds: (
    sectionIds: number[],
    options?: { include?: boolean; exclude?: boolean }
  ) => void;
};

const defaultContextValue: IInventoryEventDetailContext = {
  event: undefined,
  eventListings: null,
  selectedListingIds: [],
  selectedListing: undefined,
  selectedGroup: undefined,
  flattenedListingGroup: undefined,
  compQuantityFilters: [],
  setCompQuantityFilters: () => {},
  priceCalcQuery: null,
  listingGroupPriceCalcQuery: null,
  compListings: [],
  filterSections: [],
  compListingSectionIds: [],
  getColor: noop,
  splitChoices: {},
  isLoading: false,
  isRecommendationDeclined: false,
  onDeclineRecommendation: noop,
  onToggleRecommendingSectionIds: noop,
};

export const InventoryEventDetailContext =
  createContext<IInventoryEventDetailContext>(defaultContextValue);

export const InventoryEventDetailContextWithSelector =
  createContextWithSelector<IInventoryEventDetailContext>(defaultContextValue);

/**
 * @deprecated use the explicit context selectors to avoid unnecessary re-render
 */
export const useInventoryEventDetailContext = () => {
  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const context = useContext(InventoryEventDetailContext);
  const contextWithSelector = useContextWithSelector(
    InventoryEventDetailContextWithSelector
  );
  return hasContextWithSelectorFeature ? contextWithSelector : context;
};

export const InventoryEventDetailContextProvider = <
  T extends
    | ListingDetailsAutoPricingSectionUpdates
    | ListingGroupAutoPriceSettings = ListingDetailsAutoPricingSectionUpdates,
>({
  event,
  eventListings,
  selectedListingIds,
  selectedListing,
  selectedGroup,
  isGroupSettingsReady = true,
  children,
  sectionRowScoreRecommendations,
  isRecommendationDeclined = false,
  listingRecommendationDeclinedMutation,
}: {
  event: Event;
  eventListings?: Listing[] | null;
  selectedListingIds: number[];
  selectedListing: Listing | undefined;
  selectedGroup: ListingGroup | undefined;
  /** True once the full API pricing reset has been applied to the form for the
   *  current group. Prevents autoPricingSettingsUpdates from being computed
   *  with stale defaultValues during or after a group switch. */
  isGroupSettingsReady?: boolean;
  children: ReactNode;
  sectionRowScoreRecommendations?: SectionRowScoreRecommendations & {
    listingId: number;
  };
  isRecommendationDeclined?: boolean;
  listingRecommendationDeclinedMutation?: Actions<number>;
}) => {
  // Contexts
  const venueMapInfo = useEventMapContextVenueMapInfoSelector();
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  // State
  const [compQuantityFilters, setCompQuantityFilters] = useState<string[]>([]);

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const { watch, setValue, formState } = useFormContext<AutoPricingInputs>();

  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );

  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingImprovedPricePreivewForVeloCB
  );

  const { compListingSelectedSectionSettings } = watch();
  const input = watch();

  // Make sure the recommendation is for the selected listing
  const sanitizedScoreRecommendations = useMemo(():
    | SectionRowScoreRecommendations
    | undefined => {
    if (selectedListing == null || sectionRowScoreRecommendations == null) {
      return undefined;
    }

    const { listingId, ...res } = sectionRowScoreRecommendations;
    if (listingId !== selectedListing.id) {
      return undefined;
    }

    return res;
  }, [sectionRowScoreRecommendations, selectedListing]);

  const onUpdateSelectedSectionSettings = useCallback(
    (settings: SelectedSectionSettings | null) => {
      setValue('compListingSelectedSectionSettings', settings);
    },
    [setValue]
  );

  // !important, we are using the shared compListingSelectedSectionSettings to mark the venue map selection
  // when no listing or listing group is selected. And we want to reset the compListingSelectedSectionSettings
  // when switching from listing/listingGroup selection to no selection
  useEffect(() => {
    if (selectedListing?.id == null && selectedGroup?.ltGrpId == null) {
      const compListingSelectedSectionSettingsNew: SelectedSectionSettings = {
        sectionIdFilterFromZoneMap: false,
        includeBetterZones: false,
        rowOffset: 0,
        sectionIdFilter: [],
        rowIdFilter: [],
        sectionRowIdFilter: {},
        sectionFilterMode: null,
      };

      setValue(
        'compListingSelectedSectionSettings',
        mapToSelectedSectionSettings(compListingSelectedSectionSettingsNew) ??
          null
      );
    }
  }, [selectedGroup?.ltGrpId, selectedListing?.id, setValue]);

  const {
    recommendingSectionIds,
    onDeclineRecommendation,
    onToggleRecommendingSectionIds,
  } = usePricingSectionScoreRecommendations(
    input.compListingSelectedSectionSettings,
    onUpdateSelectedSectionSettings,
    sanitizedScoreRecommendations?.recommendedSelectedSectionSettings,
    venueMapInfo?.sections ?? [],
    selectedListing,
    listingRecommendationDeclinedMutation,
    !(selectedListing?.id && hasSelectedSectionScoreRecommendationsFeature)
  );

  const flattenedListingGroup: Listing[] | undefined = useMemo(() => {
    if (!selectedGroup) {
      return;
    }

    return flattenListingGroup(selectedGroup)
      .map((l) => l as Listing)
      .filter((l) => l.availQty > 0);
  }, [selectedGroup]);

  const sanitizedListingInput = useMemo(():
    | ListingPricingInputWithId
    | undefined => {
    if (selectedGroup || !input) {
      return undefined;
    }

    const {
      id,
      netProceedsFloor,
      netProceedsCeiling,
      autoPricingEnabled,
      currencyCode,

      compListingFloor,
      compListingCeiling,
      compListingMode,
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
      compListingExcludeFanInventory,
      compListingExcludeDefects,
      compListingIncludeUnmappedInventory,
      compListingQuantityScoreAdjustmentEnabled,
      compListingQuantityScoreAdjustmentOverrideJson,
      compListingSelectedSectionSettings,
      compListingQuantityFilters,

      undercutMode,
      undercutAbsoluteAmount,
      undercutRelativeAmount,
      undercutReferenceListingPosition,

      circuitBreakerMaxDiscountVelocityPercent,
      circuitBreakerMaxDiscountVelocityTicksInHours,
      circuitBreakerMinCompListingCount,
      circuitBreakerRelativeCeiling,
      circuitBreakerRelativeFloor,
      velocityCircuitBreakerStateRowVersion,

      outlierMode,
      outlierStandardDeviations,
      outlierKthLowestLimit,
      outlierKthLowestLimitRelativeSpacing,
      outlierKthLowestLimitAbsoluteSpacing,
    } = input as ListingDetailsAutoPricingSectionUpdates;

    const mergedInput = {
      id,
      netProceedsFloor,
      netProceedsCeiling,
      autoPricingEnabled,
      currencyCode,

      compListingFloor,
      compListingCeiling,
      compListingMode,
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
      compListingExcludeFanInventory,
      compListingExcludeDefects,
      compListingIncludeUnmappedInventory,
      compListingQuantityScoreAdjustmentEnabled,
      compListingQuantityScoreAdjustmentOverrideJson,
      compListingSelectedSectionSettings,
      compListingQuantityFilters,

      undercutMode,
      undercutAbsoluteAmount,
      undercutRelativeAmount,
      undercutReferenceListingPosition,

      circuitBreakerMaxDiscountVelocityPercent,
      circuitBreakerMaxDiscountVelocityTicksInHours,
      circuitBreakerMinCompListingCount,
      circuitBreakerRelativeCeiling,
      circuitBreakerRelativeFloor,

      outlierMode,
      outlierStandardDeviations,
      outlierKthLowestLimit,
      outlierKthLowestLimitRelativeSpacing,
      outlierKthLowestLimitAbsoluteSpacing,
    } as ListingPricingInputWithId;

    if (hasVeloCBFeature) {
      mergedInput.velocityCircuitBreakerStateRowVersion =
        velocityCircuitBreakerStateRowVersion;
    }

    return mergedInput;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, input, hasVeloCBFeature]);

  // [NOTE]
  //
  // The below is masquerading as a ListingPriceCalculationsInEventInput object
  // because we don't have a true price preview endpoint that is specifically built
  // around the one pager listing group form.
  const autoPricingSettingsUpdates = useMemo(():
    | ListingPriceCalculationsInEventInput
    | undefined => {
    if (!selectedGroup || !formState.defaultValues) {
      return undefined;
    }

    return getListingPriceCalculationsInEventInputFromGroupSettings(
      input as ListingGroupAutoPriceSettings,
      formState.defaultValues,
      uiCurrency.code
    );
  }, [selectedGroup, formState.defaultValues, input, uiCurrency.code]);

  const { priceCalcQuery } = useAutoPricingPreview(
    event?.viagId ?? null,
    selectedListing?.id,
    sanitizedListingInput
  );

  const { listingCalculationsQuery: listingGroupPriceCalcQuery } =
    useListingGroupAutoPricePreview(
      event,
      flattenedListingGroup ?? [],
      autoPricingSettingsUpdates,
      isGroupSettingsReady
    );
  const { compListingsQuery } = useGetCompListings(event);

  const formSectionFilterIds = useMemo(() => {
    return compListingSelectedSectionSettings?.sectionIdFilter ?? [];
  }, [compListingSelectedSectionSettings?.sectionIdFilter]);

  const compListings = useMemo(() => {
    if (selectedListing?.id && hasAutoPricingFeature) {
      return priceCalcQuery?.data?.compListings;
    }

    if (isEmpty(formSectionFilterIds)) {
      if (selectedGroup?.ltGrpId && hasAutoPricingFeature) {
        return listingGroupPriceCalcQuery?.data?.compListings;
      }
      return compListingsQuery.data ?? undefined;
    }

    if (selectedGroup?.ltGrpId && hasAutoPricingFeature) {
      return listingGroupPriceCalcQuery?.data?.compListings?.filter((l) => {
        const { section } = getMatchingSectionRow(l, venueMapInfo?.sections);
        return section && formSectionFilterIds.includes(section.id);
      });
    }

    return compListingsQuery.data?.filter((l) => {
      const { section } = getMatchingSectionRow(l, venueMapInfo?.sections);
      return section && formSectionFilterIds.includes(section.id);
    });
  }, [
    selectedListing?.id,
    hasAutoPricingFeature,
    formSectionFilterIds,
    selectedGroup?.ltGrpId,
    compListingsQuery.data,
    priceCalcQuery?.data?.compListings,
    listingGroupPriceCalcQuery?.data?.compListings,
    venueMapInfo?.sections,
  ]);

  const filterSections = useMemo(() => {
    return (
      venueMapInfo?.sections?.filter(({ id }) =>
        formSectionFilterIds.includes(id)
      ) ?? []
    );
  }, [formSectionFilterIds, venueMapInfo?.sections]);

  // marked section ids in the venue map
  const compListingSectionIds = useMemo(() => {
    if (isEmpty(selectedListingIds)) {
      return formSectionFilterIds;
    }

    const sectionNames = (compListings ?? []).map((l) => l.section);
    const sectionIds = (venueMapInfo?.sections ?? [])
      .filter((section) => sectionNames.includes(section.name))
      .map(({ id }) => id);

    return union(sectionIds, formSectionFilterIds);
  }, [
    selectedListingIds,
    compListings,
    venueMapInfo?.sections,
    formSectionFilterIds,
  ]);

  const canSetPrice = useUserCanSetPrice(selectedListing, false);

  const compListingsFiltered = useMemo(() => {
    if (isEmpty(compQuantityFilters)) {
      return compListings;
    }

    return compListings?.filter(({ validPurchaseQuantities }) => {
      if (isEmpty(validPurchaseQuantities)) {
        return true;
      }
      return validPurchaseQuantities.some(
        (quantity) => compQuantityFilters.indexOf(quantity + '') > -1
      );
    });
  }, [compListings, compQuantityFilters]);

  const getColor = useComparableListingsVenueMapColor(
    eventListings ?? [],
    compListingsFiltered,
    compListings
  );

  const splitChoices = useMemo(() => {
    return uniq(
      (compListings ?? []).flatMap(
        (listing) => listing.validPurchaseQuantities ?? []
      )
    ).reduce(
      (res, quantity) => {
        const stringQuantity = quantity + '';
        res[stringQuantity] = stringQuantity;
        return res;
      },
      {} as Record<string, string>
    );
  }, [compListings]);

  const isLoading = useMemo(
    () =>
      compListingsQuery.isLoading ||
      priceCalcQuery.isLoading ||
      listingGroupPriceCalcQuery.isLoading,
    [
      compListingsQuery.isLoading,
      priceCalcQuery.isLoading,
      listingGroupPriceCalcQuery.isLoading,
    ]
  );

  const contextValue = useMemo(
    () => ({
      event,
      eventListings,
      selectedListingIds,
      recommendingSectionIds,
      selectedListing,
      selectedGroup,
      flattenedListingGroup,
      compQuantityFilters,
      setCompQuantityFilters,
      priceCalcQuery,
      listingGroupPriceCalcQuery,
      compListings: compListingsFiltered,
      filterSections,
      compListingSectionIds,
      getColor,
      splitChoices,
      isLoading,
      sectionRowScoreRecommendations: sanitizedScoreRecommendations,
      isRecommendationDeclined,
      onDeclineRecommendation,
      onToggleRecommendingSectionIds,
    }),
    [
      compListingSectionIds,
      compListingsFiltered,
      compQuantityFilters,
      event,
      eventListings,
      filterSections,
      flattenedListingGroup,
      getColor,
      isLoading,
      isRecommendationDeclined,
      listingGroupPriceCalcQuery,
      onDeclineRecommendation,
      onToggleRecommendingSectionIds,
      priceCalcQuery,
      recommendingSectionIds,
      sanitizedScoreRecommendations,
      selectedGroup,
      selectedListing,
      selectedListingIds,
      splitChoices,
    ]
  );

  if (!canSetPrice) {
    return null;
  }

  if (hasContextWithSelectorFeature) {
    return (
      <InventoryEventDetailContextWithSelector.Provider value={contextValue}>
        {children}
      </InventoryEventDetailContextWithSelector.Provider>
    );
  }

  return (
    <InventoryEventDetailContext.Provider value={contextValue}>
      {children}
    </InventoryEventDetailContext.Provider>
  );
};
